import { useState } from "react";
import PropTypes from "prop-types";
import styled, { useTheme } from "styled-components";
import { Link } from "react-router-dom";
import texmuIconLight from "../assets/Texmu-iOS-Default-1024x1024@1x.png";
import texmuIconDark from "../assets/Texmu-iOS-Dark-1024x1024@1x.png";

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(14px);
  background: ${({ theme }) => theme.navBg};
  box-shadow: ${({ theme }) => theme.navShadow};
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

const NavInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  position: relative;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 700;
  font-size: 20px;
`;

const LogoBadge = styled.span`
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const LogoImage = styled.img`
  width: 48px;
  height: 48px;
  display: block;
  border-radius: 12px;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileControls = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const MobileMenuToggle = styled.button`
  border: none;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
  }
`;

const MobileMenu = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${({ $open }) => ($open ? "grid" : "none")};
    position: absolute;
    top: calc(100% + 12px);
    left: 24px;
    right: 24px;
    padding: 18px;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ theme }) => theme.surface};
    border: 1px solid ${({ theme }) => theme.border};
    box-shadow: 0 18px 36px rgba(14, 22, 36, 0.18);
    gap: 12px;
  }
`;

const MobileNavLink = styled.a`
  display: block;
  font-size: 16px;
  font-weight: 500;
  padding: 10px 0;
  color: ${({ theme }) => theme.text};

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.accent};
    outline: none;
  }
`;

const NavLink = styled.a`
  font-size: 15px;
  color: ${({ theme }) => theme.muted};
  font-weight: 500;
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: color 0.2s ease, background 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.border};
  }
`;

const ThemeToggle = styled.button`
  border: none;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  padding: 8px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
  }
`;

const MenuThemeToggle = styled(ThemeToggle)`
  @media (max-width: 768px) {
    display: inline-flex;
    justify-content: center;
    width: 100%;
  }
`;

function NavBar({ sections, themeName, onToggleTheme }) {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const logoSrc = theme.name === "dark" ? texmuIconDark : texmuIconLight;
  const isLight = themeName === "light";
  const label = isLight ? "Switch to dark mode" : "Switch to light mode";

  const handleToggleMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const handleCloseMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <Nav>
      <NavInner>
        <Logo to="/">
          <LogoBadge aria-hidden="true">
            <LogoImage src={logoSrc} alt="Texmu logo" />
          </LogoBadge>
          <span>Texmu</span>
        </Logo>
        <DesktopActions>
          <NavLinks>
            {sections.map((item) => (
              <NavLink key={item.href} href={`/${item.href}`}>
                {item.label}
              </NavLink>
            ))}
          </NavLinks>
          <ThemeToggle
            type="button"
            onClick={onToggleTheme}
            aria-label={label}
            aria-pressed={!isLight}
          >
            {isLight ? "🌙 Dark" : "☀️ Light"}
          </ThemeToggle>
        </DesktopActions>
        <MobileControls>
          <MobileMenuToggle
            type="button"
            onClick={handleToggleMenu}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {isMobileMenuOpen ? "Close" : "Menu"}
          </MobileMenuToggle>
        </MobileControls>
        <MobileMenu id="mobile-navigation" $open={isMobileMenuOpen}>
          {sections.map((item) => (
            <MobileNavLink key={`${item.href}-mobile`} href={`/${item.href}`} onClick={handleCloseMenu}>
              {item.label}
            </MobileNavLink>
          ))}
          <MenuThemeToggle
            type="button"
            onClick={() => {
              onToggleTheme();
              handleCloseMenu();
            }}
            aria-label={label}
            aria-pressed={!isLight}
          >
            {isLight ? "🌙 Dark" : "☀️ Light"}
          </MenuThemeToggle>
        </MobileMenu>
      </NavInner>
    </Nav>
  );
}

NavBar.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired
    })
  ).isRequired,
  themeName: PropTypes.string.isRequired,
  onToggleTheme: PropTypes.func.isRequired
};

export default NavBar;
